import { Tooltip } from '@material-ui/core';
import { Inline, Stack, Tag } from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { startCase } from 'es-toolkit/compat';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CrownIcon } from '../assets/CrownIcon';
import { formatLoadStatus, isLoadCanceled, LoadDTO } from '../data/LoadDTO';
import { useFlag } from 'shared/settings/FeatureToggles';
import { LoadInspectionSyncHeaderStatus } from '../view/LoadInspectionSyncHeaderStatus';
import { isLoadPhotoSyncPendingOnList } from '../view/loadPhotoSyncStatus';
import { useSubhaulLoad } from '../view/useSubhaulLoad';

const PreferredProgramIcon = styled(CrownIcon)`
  margin-bottom: -3px;
  margin-top: -3px;
`;

const TripTag = styled(Tag)`
  cursor: pointer;
`;

interface LoadTagsProps {
  load: LoadDTO;
  showDelayedDays?: boolean;
  isLoadsList?: boolean;
}

export function LoadTags({
  load,
  showDelayedDays = false,
  isLoadsList = false,
}: LoadTagsProps) {
  const navigate = useNavigate();
  const shouldShowVehiclesPage = useFlag('ctms_edi_vehicles');
  const shouldShowUnassigned = useFlag('ctms_loads_new');
  const { isSubhauled } = useSubhaulLoad();
  const currentTerminal = useMemo(
    () => load.terminals?.find((terminal) => terminal.is_current),
    [load.terminals],
  );
  const isChangedByBroker =
    load.activities?.some((item) => item.is_changed_by_broker) ||
    load.is_changed_by_broker;

  const shouldDisplaySyncStatus =
    isLoadsList && isLoadPhotoSyncPendingOnList(load);

  return (
    <Inline space="xsmall">
      {load.inspection_type === 'aiag' && (
        <Tag color="purple" variant="bold" aria-label="AIAG">
          AIAG
        </Tag>
      )}

      {load.is_edi && shouldShowVehiclesPage && (
        <Tag color="green" variant="bold" aria-label="EDI">
          EDI
        </Tag>
      )}

      {load.inspection_type === 'advanced' && (
        <Tag color="teal" variant="bold" aria-label="Advanced">
          Advanced
        </Tag>
      )}

      {load.is_instant_dispatch && (
        <Tooltip
          title={
            <Box padding="xsmall">
              <Stack space="small">
                <TextBox color="white" variant="heading-4">
                  Exclusive Load for Preferred Carriers
                </TextBox>
                <Stack space="xsmall">
                  <li>
                    On-time pickups and deliveries improve ratings and bring
                    more loads for you.
                  </li>
                  <li>Delays lower scores and remove exclusive access.</li>
                </Stack>
              </Stack>
            </Box>
          }
        >
          <Tag color="yellow" variant="bold" aria-label="Preferred Program">
            <Inline verticalAlign="center" space="xxsmall">
              <PreferredProgramIcon />
              Preferred Program
            </Inline>
          </Tag>
        </Tooltip>
      )}

      {load.is_dispatched_to_carrier && (
        <Tag color="purple" variant="subtle" aria-label="Dispatched To Carrier">
          Dispatched to Carrier
        </Tag>
      )}

      {load.is_edi && isSubhauled && (
        <Tag aria-label="subhauled tag" variant="subtle" color="purple">
          Sub-hauled
        </Tag>
      )}

      {currentTerminal ? (
        <Tag color="purple" variant="bold" aria-label="Terminal">
          In {currentTerminal.name}
        </Tag>
      ) : (
        <LoadInspectionSyncHeaderStatus
          load={load}
          shouldDisplaySyncStatus={shouldDisplaySyncStatus}
          statusLabel={formatLoadStatus(
            load.status,
            false,
            undefined,
            shouldShowUnassigned,
            !!load.driver?.email,
          )}
        />
      )}

      {showDelayedDays && load.delayed_days && load.delayed_days > 0 && (
        <Tag color="yellow" variant="subtle">
          {load.delayed_days} days
        </Tag>
      )}

      {load.archived && load.stage !== 'deleted' && (
        <Tag color="grey" variant="subtle" aria-label="Archived">
          Archived
        </Tag>
      )}

      {load.is_canceled_by_broker ? (
        <Tag color="red" variant="bold" aria-label="Cancelled By Shipper">
          Canceled by Shipper
        </Tag>
      ) : isLoadCanceled(load) ? (
        <Tooltip
          title={`Canceled by ${load.is_canceled_by === 'prime_carrier' ? 'Customer' : startCase(load.is_canceled_by || '')}`}
          aria-label="Canceled"
        >
          <Tag color="red" variant="bold">
            Canceled
          </Tag>
        </Tooltip>
      ) : null}

      {!!load.trip?.name && (
        <Tooltip
          placement="top"
          arrow={true}
          title="Now you can open a trip by clicking the trip name"
        >
          <TripTag
            color="grey"
            variant="subtle"
            onClick={() => {
              navigate(`/trips/view/${load.trip?.guid}`);
            }}
          >
            {load.trip.name}
          </TripTag>
        </Tooltip>
      )}
      {isChangedByBroker && !load.archived && load.is_active && (
        <Tag color="grey" variant="subtle">
          Updated by Shipper
        </Tag>
      )}
    </Inline>
  );
}
